import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Calendar, Clock, MapPin, Users, Share2, Trash2, LogOut,
  Plus, Trophy, Crown, Hourglass, Swords, CheckCircle2, CalendarCheck, MessageSquare,
  Pencil, UserMinus, ArrowUpCircle, Star,
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar } from "./Avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { formatDateLong, confirmedAndWaitlist } from "../lib/padel";
import { useAuth } from "../lib/auth";
import { shareResult } from "../lib/resultImage";
import { toast } from "sonner";

export const MatchCard = ({ match, playerName, photos = {}, onJoin, onLeave, onShare, onDelete, onRecordResult, onToggleConfirm, onOpenComments, onKick, onPromote, onEdit, onOpenProfile, onOpenRatings, index }) => {
  const { confirmed, waitlist } = confirmedAndWaitlist(match);
  const isFull = confirmed.length >= match.total_slots;
  const amIn = match.players.some((p) => p.name.toLowerCase() === (playerName || "").toLowerCase());
  const myIndex = match.players.findIndex((p) => p.name.toLowerCase() === (playerName || "").toLowerCase());
  const amWaitlisted = amIn && myIndex >= match.total_slots;
  const { user } = useAuth();
  const isOrganizer = user?.role === "admin" || (match.organizer || "").toLowerCase() === (playerName || "").toLowerCase();
  const [busy, setBusy] = useState(false);
  const canRecord = match.total_slots === 4 && confirmed.length === 4;
  const result = match.result;
  const confirmations = match.confirmations || [];
  const isConfirmed = (name) => confirmations.some((c) => c.toLowerCase() === name.toLowerCase());
  const amConfirmedPlayer = amIn && !amWaitlisted;
  const iConfirmed = isConfirmed(playerName || "");

  const photoOf = (name) => photos[name.toLowerCase()];

  const slots = Array.from({ length: match.total_slots });

  const handle = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      data-testid={`match-card-${match.id}`}
      className="rounded-3xl bg-[#121b2d] border border-[#1e2c4a] overflow-hidden shadow-xl shadow-black/40"
    >
      {/* header strip */}
      <div className="relative px-5 pt-5 pb-4 border-b border-[#1e2c4a]">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[#ccff00] font-heading font-extrabold text-lg">
              <MapPin className="w-4 h-4 shrink-0" />
              <span className="truncate" data-testid={`match-venue-${match.id}`}>{match.venue}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-300">
              <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-slate-500" />{formatDateLong(match.date)}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-slate-500" />{match.time}</span>
            </div>
          </div>
          <span
            className={`shrink-0 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
              result ? "bg-[#25D366]/15 text-[#25D366]" : isFull ? "bg-[#ff9f1c]/15 text-[#ff9f1c]" : "bg-[#ccff00]/15 text-[#ccff00]"
            }`}
            data-testid={`match-status-${match.id}`}
          >
            {result ? "Giocata" : isFull ? "Completa" : `${confirmed.length}/${match.total_slots}`}
          </span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {match.court_type && <Chip>{match.court_type}</Chip>}
          {match.level && <Chip>🎯 {match.level}</Chip>}
          {match.price && <Chip>💶 {match.price}</Chip>}
        </div>
      </div>

      {/* court slots */}
      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
            <Users className="w-4 h-4" /> Giocatori
          </div>
          {confirmations.length > 0 && (
            <span className="flex items-center gap-1 text-xs font-bold text-[#25D366]" data-testid={`confirm-count-${match.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> {confirmations.length} present{confirmations.length === 1 ? "e" : "i"}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {slots.map((_, i) => {
            const p = confirmed[i];
            if (p) {
              const isMe = p.name.toLowerCase() === (playerName || "").toLowerCase();
              const canKick = isOrganizer && p.name.toLowerCase() !== (match.organizer || "").toLowerCase() && !result;
              return (
                <div
                  key={i}
                  data-testid={`match-slot-${match.id}-${i}`}
                  className={`flex items-center gap-2 rounded-xl px-2.5 py-2 border ${
                    isMe ? "bg-[#ccff00]/10 border-[#ccff00]/40" : "bg-slate-900/60 border-slate-700/60"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onOpenProfile?.(p.name)}
                    data-testid={`profile-link-${match.id}-${i}`}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-80 transition-opacity"
                  >
                    <span className="relative shrink-0">
                      <Avatar name={p.name} photoPath={photoOf(p.name)} size={28} />
                      {i === 0 && <Crown className="w-3.5 h-3.5 text-[#ccff00] absolute -top-2 -right-1.5 drop-shadow" data-testid={`organizer-crown-${match.id}`} />}
                    </span>
                    <span className="text-[13px] font-semibold text-slate-100 leading-tight min-w-0 [overflow-wrap:anywhere]">
                      {p.name}
                    </span>
                  </button>
                  {isConfirmed(p.name) && (
                    <CheckCircle2 className="w-4 h-4 text-[#25D366] shrink-0" data-testid={`confirmed-${match.id}-${i}`} />
                  )}
                  {canKick && (
                    <button
                      type="button"
                      onClick={() => onKick?.(match, p.name)}
                      data-testid={`kick-player-btn-${match.id}-${i}`}
                      title="Rimuovi giocatore"
                      className="text-slate-500 hover:text-[#ff4d4d] shrink-0"
                    >
                      <UserMinus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            }
            return (
              <button
                key={i}
                disabled={busy || amIn}
                onClick={() => handle(() => onJoin(match))}
                data-testid={`join-slot-${match.id}-${i}`}
                className="flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 border border-dashed border-[#ccff00]/40 text-[#ccff00] text-sm font-semibold hover:bg-[#ccff00]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed min-h-[46px]"
              >
                <Plus className="w-4 h-4" /> Libero
              </button>
            );
          })}
        </div>

        {waitlist.length > 0 && (
          <div className="mt-3 rounded-xl bg-[#ff9f1c]/10 border border-[#ff9f1c]/25 px-3 py-2.5">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#ff9f1c] mb-1.5">
              <Hourglass className="w-3.5 h-3.5" /> Lista d'attesa
            </div>
            <div className="flex flex-col gap-1.5">
              {waitlist.map((p, i) => (
                <div key={p.name} className="flex items-center justify-between gap-2 bg-slate-900/60 rounded-full pl-3 pr-1.5 py-1">
                  <button
                    type="button"
                    onClick={() => onOpenProfile?.(p.name)}
                    data-testid={`waitlist-profile-${match.id}-${i}`}
                    className="text-xs text-slate-200 text-left leading-tight min-w-0 [overflow-wrap:anywhere] hover:text-[#ccff00] transition-colors"
                  >
                    #{i + 1} {p.name}
                  </button>
                  {isOrganizer && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => onPromote?.(match, p.name)}
                        data-testid={`promote-player-btn-${match.id}-${i}`}
                        title="Promuovi tra i convocati"
                        className="text-[#ccff00] hover:text-[#aeea00]"
                      >
                        <ArrowUpCircle className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onKick?.(match, p.name)}
                        data-testid={`waitlist-kick-btn-${match.id}-${i}`}
                        title="Rimuovi dalla lista"
                        className="text-slate-500 hover:text-[#ff4d4d]"
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {match.notes && (
          <p className="mt-3 text-sm text-slate-400 italic">📝 {match.notes}</p>
        )}

        {match.booking_link && (
          /^https?:\/\//i.test(match.booking_link.trim()) ? (
            <a
              href={match.booking_link.trim()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`booking-link-${match.id}`}
              className="mt-3 flex items-center gap-2 text-sm font-semibold text-[#00e5ff] bg-[#00e5ff]/10 border border-[#00e5ff]/25 rounded-xl px-3 py-2.5 hover:bg-[#00e5ff]/15 transition-colors"
            >
              <CalendarCheck className="w-4 h-4 shrink-0" /> Prenota il campo
            </a>
          ) : (
            <p className="mt-3 flex items-center gap-2 text-sm text-[#00e5ff]" data-testid={`booking-note-${match.id}`}>
              <CalendarCheck className="w-4 h-4 shrink-0" /> {match.booking_link}
            </p>
          )
        )}

        {result && (
          <div className="mt-3 rounded-xl bg-[#ccff00]/8 border border-[#ccff00]/25 px-3 py-2.5" data-testid={`match-result-${match.id}`}>
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#ccff00] mb-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Risultato
            </div>
            <div className="space-y-1.5 text-sm">
              {[["A", result.team_a, "text-[#ccff00]"], ["B", result.team_b, "text-[#00e5ff]"]].map(([side, team, color]) => (
                <div key={side} className="flex items-center gap-2" data-testid={`match-result-team-${side.toLowerCase()}-${match.id}`}>
                  <span className={`flex-1 min-w-0 font-semibold break-words leading-snug ${result.winner === side ? color : "text-slate-300"}`}>
                    {result.winner === side && <Trophy className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
                    {team.join(" & ")}
                  </span>
                  <span className="flex gap-1 shrink-0">
                    {result.sets.map((s, i) => {
                      const mine = side === "A" ? s.a : s.b;
                      const other = side === "A" ? s.b : s.a;
                      return (
                        <span key={i} className={`w-7 h-7 rounded-md flex items-center justify-center font-heading font-bold ${mine > other ? "bg-[#ccff00]/15 text-slate-100" : "bg-slate-800/60 text-slate-400"}`}>
                          {mine}
                        </span>
                      );
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* actions */}
      <div className="px-5 pb-5 flex flex-wrap gap-2">
        {amConfirmedPlayer && !result && (
          <Button
            onClick={() => handle(() => onToggleConfirm(match, !iConfirmed))}
            disabled={busy}
            data-testid={`confirm-presence-btn-${match.id}`}
            className={`w-full font-bold rounded-xl h-11 border ${
              iConfirmed
                ? "bg-[#25D366]/15 text-[#25D366] border-[#25D366]/40 hover:bg-[#25D366]/20"
                : "bg-[#1e2c4a] text-slate-100 border-slate-600 hover:bg-[#263a5f]"
            }`}
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {iConfirmed ? "Presenza confermata" : "Conferma presenza"}
          </Button>
        )}
        {result && (
          <div className="grid grid-cols-2 gap-2 w-full basis-full">
            <Button
              onClick={() => onOpenRatings?.(match)}
              data-testid={`ratings-btn-${match.id}`}
              className="w-full bg-[#1e2c4a] hover:bg-[#263a5f] text-[#ccff00] font-bold rounded-xl h-11 border border-[#ccff00]/30"
            >
              <Star className="w-4 h-4 mr-1.5" /> Pagelle
            </Button>
            <Button
              onClick={async () => {
                const out = await shareResult(match).catch(() => "error");
                if (out === "downloaded") toast.success("Immagine scaricata: allegala nel gruppo WhatsApp");
                if (out === "error") toast.error("Condivisione non riuscita");
              }}
              data-testid={`share-result-btn-${match.id}`}
              className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-xl h-11"
            >
              <Share2 className="w-4 h-4 mr-1.5" /> Risultato
            </Button>
          </div>
        )}
        {canRecord && !result && (
          <Button
            onClick={() => onRecordResult(match)}
            data-testid={`record-result-btn-${match.id}`}
            className="w-full bg-[#1e2c4a] hover:bg-[#263a5f] text-[#ccff00] font-bold rounded-xl h-11 border border-[#ccff00]/30"
          >
            <Swords className="w-4 h-4 mr-1.5" /> Registra risultato
          </Button>
        )}
        {!amIn && !result && user?.role !== "admin" && (
          <Button
            disabled={busy}
            onClick={() => handle(() => onJoin(match))}
            data-testid={`join-match-btn-${match.id}`}
            className="flex-1 min-w-[130px] bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-xl h-11"
          >
            <Trophy className="w-4 h-4 mr-1.5" />
            {isFull ? "Entra in lista" : "Partecipa"}
          </Button>
        )}
        {amIn && !result && (
          <Button
            disabled={busy}
            variant="outline"
            onClick={() => handle(() => onLeave(match))}
            data-testid={`leave-match-btn-${match.id}`}
            className="flex-1 min-w-[130px] bg-transparent border-[#ff4d4d]/40 text-[#ff4d4d] hover:bg-[#ff4d4d]/10 hover:text-[#ff4d4d] font-semibold rounded-xl h-11"
          >
            <LogOut className="w-4 h-4 mr-1.5" />
            {amWaitlisted ? "Esci dall'attesa" : "Annulla"}
          </Button>
        )}
        {!result && (
        <Button
          onClick={() => onShare(match)}
          data-testid={`share-whatsapp-btn-${match.id}`}
          className="flex-1 min-w-[130px] bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-xl h-11"
        >
          <Share2 className="w-4 h-4 mr-1.5" /> WhatsApp
        </Button>
        )}
        <Button
          onClick={() => onOpenComments(match)}
          variant="outline"
          data-testid={`comments-btn-${match.id}`}
          className="flex-1 min-w-[130px] bg-transparent border-[#1e2c4a] text-slate-200 hover:bg-slate-800 hover:text-white font-semibold rounded-xl h-11"
        >
          <MessageSquare className="w-4 h-4 mr-1.5" /> Commenti
          {(match.comments || []).length > 0 && (
            <span className="ml-1.5 text-xs font-bold bg-[#ccff00] text-[#090d16] rounded-full px-1.5" data-testid={`comments-count-${match.id}`}>
              {match.comments.length}
            </span>
          )}
        </Button>
        {isOrganizer && (
          <Button
            onClick={() => onEdit?.(match)}
            variant="ghost"
            size="icon"
            data-testid={`edit-match-btn-${match.id}`}
            title="Modifica partita"
            className="h-11 w-11 text-slate-500 hover:text-[#ccff00] hover:bg-[#ccff00]/10 rounded-xl shrink-0"
          >
            <Pencil className="w-4 h-4" />
          </Button>
        )}
        {isOrganizer && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              data-testid={`delete-match-btn-${match.id}`}
              className="h-11 w-11 text-slate-500 hover:text-[#ff4d4d] hover:bg-[#ff4d4d]/10 rounded-xl shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100">
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminare la partita?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                L'operazione è definitiva e tutti gli iscritti verranno rimossi.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white">Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(match)}
                data-testid={`confirm-delete-btn-${match.id}`}
                className="bg-[#ff4d4d] hover:bg-[#e63939] text-white"
              >
                Elimina
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        )}
      </div>
    </motion.div>
  );
};

const Chip = ({ children }) => (
  <span className="text-xs font-medium text-slate-300 bg-slate-900/60 border border-slate-700/50 rounded-full px-2.5 py-1">
    {children}
  </span>
);
